import * as d3 from 'd3';
import { ScreenSize, TimeRange } from './types';
import { useRef, useEffect } from 'react';
import { useCurrent } from '../../../helpers/hooks';
import { throttle } from 'lodash';

interface Props {
  size: ScreenSize;
  xScale: d3.ScaleTime<number, number>;
  zoomRange?: TimeRange;
  onZoom?: (zoomRange: TimeRange) => void;
  onZoomEnd?: (zoomRange: TimeRange) => void;
  onResetZoom?: () => void;
}

const minZoomDuration = 5000;

const enforceMin = (range: TimeRange) => {
  const diff = range.end.getTime() - range.start.getTime();
  if (Math.abs(diff) < minZoomDuration) {
    return {
      start: range.start,
      end: new Date(range.start.getTime() + minZoomDuration),
    };
  } else {
    return range;
  }
};

export const ZoomBar = (props: Props) => {
  const { zoomRange, size, onResetZoom } = props;
  const xScale = useCurrent(props.xScale);
  const onZoom = useCurrent(props.onZoom);
  const onZoomEnd = useCurrent(props.onZoomEnd);

  const groupRef = useRef<SVGGElement>(null);
  const brushRef = useRef<SVGGElement>(null);
  const brushInstRef = useRef<d3.BrushBehavior<number>>();
  const isBrushingRef = useRef<boolean>(false);

  if (!brushInstRef.current && groupRef.current) {
    const brush = d3.brushX<number>().extent([
      [0, 0],
      [size.width, size.height],
    ]);
    const g = d3.select(groupRef.current);

    brush.on('brush', function () {
      isBrushingRef.current = true;
      const selection = d3.brushSelection(this);
      if (
        selection &&
        typeof selection[0] === 'number' &&
        typeof selection[1] === 'number' &&
        onZoom.current
      ) {
        const throttledZoom = throttle(onZoom.current, 50);
        throttledZoom(
          enforceMin({
            start: xScale.current.invert(selection[0]),
            end: xScale.current.invert(selection[1]),
          })
        );
      }
    });

    brush.on('end', function () {
      const selection = d3.brushSelection(this);
      isBrushingRef.current = false;

      if (selection === null) {
        onResetZoom?.();
      } else if (
        selection &&
        typeof selection[0] === 'number' &&
        typeof selection[1] === 'number' &&
        onZoomEnd.current
      ) {
        onZoomEnd.current(
          enforceMin({
            start: xScale.current.invert(selection[0]),
            end: xScale.current.invert(selection[1]),
          })
        );
        // @ts-ignore todo: fix type
        d3.select(this).call(brush.move, null);
      }
    });
    brushInstRef.current = brush;
    // @ts-ignore todo: fix type
    g.selectAll('.brush').call(brush);
  }

  // We want to set the posistion of the zoom rect manually if we are not
  // brushing and the component size changes or the zoomRange is updated
  // externaly.
  useEffect(() => {
    const currentXScale = xScale.current;

    if (groupRef.current && brushInstRef.current && brushRef.current && !isBrushingRef.current) {
      const brushEl = brushRef.current;
      const brush = brushInstRef.current;
      brush.extent([
        [0, 0],
        [size.width, size.height],
      ]);

      const brushSelection = d3.select(groupRef.current).selectAll<SVGGElement, unknown>('.brush');
      brushSelection.call(brush as d3.BrushBehavior<unknown>);

      if (zoomRange && brushEl) {
        const newSelection = [currentXScale(zoomRange.start), currentXScale(zoomRange.end)];

        const currentSelection = d3.brushSelection(brushEl);
        if (
          !currentSelection ||
          currentSelection[0] !== newSelection[0] ||
          currentSelection[1] !== newSelection[1]
        ) {
          d3.select<SVGGElement, unknown>(brushEl).call(
            brush.move as unknown as (
              selection: d3.Selection<SVGGElement, unknown, null, undefined>,
              value: null | [number, number]
            ) => void,
            newSelection
          );
        }
      } else {
        d3.select<SVGGElement, unknown>(brushEl).call(
          brush.move as unknown as (
            selection: d3.Selection<SVGGElement, unknown, null, undefined>,
            value: null | [number, number]
          ) => void,
          null
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoomRange, size.width, size.height]);

  useEffect(() => {
    if (brushRef.current && brushInstRef.current && zoomRange) {
      const brushEl = brushRef.current;
      const currentXScale = xScale.current;
      const initialSelection = [currentXScale(zoomRange.start), currentXScale(zoomRange.end)];

      d3.select<SVGGElement, unknown>(brushEl).call(
        brushInstRef.current.move as unknown as (
          selection: d3.Selection<SVGGElement, unknown, null, undefined>,
          value: null | [number, number]
        ) => void,
        initialSelection
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brushInstRef.current]);

  return (
    <g ref={groupRef}>
      <g className="brush" ref={brushRef} />
    </g>
  );
};
